import React, { useEffect, useRef, useState } from 'react';
import { Form, Button, ListGroup, Modal, Alert } from 'react-bootstrap';
import { FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { reverseGeocode, searchLocations } from '../utils/geocoding';

const LocationSelector = ({ label, location, onSelect }) => {
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [manualLocation, setManualLocation] = useState('');
  const [locationError, setLocationError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isResolvingCurrentLocation, setIsResolvingCurrentLocation] = useState(false);
  const [isResolvingManualLocation, setIsResolvingManualLocation] = useState(false);
  const [searchTouched, setSearchTouched] = useState(false);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();

    if (!showModal) {
      return undefined;
    }

    if (trimmedQuery.length < 3) {
      setSuggestions([]);
      setIsSearching(false);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setIsSearching(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await searchLocations(trimmedQuery);
        if (searchRequestRef.current === requestId) {
          setSuggestions(results);
          setLocationError('');
        }
      } catch (error) {
        if (searchRequestRef.current === requestId) {
          setSuggestions([]);
          setLocationError(error.message || 'Unable to search locations right now.');
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setIsSearching(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery, showModal]);

  const resetModalState = () => {
    setShowModal(false);
    setSearchQuery('');
    setSuggestions([]);
    setManualLocation('');
    setLocationError('');
    setSearchTouched(false);
    setIsSearching(false);
    setIsResolvingCurrentLocation(false);
    setIsResolvingManualLocation(false);
  };

  const handleCurrentLocation = () => {
    if ('geolocation' in navigator) {
      setIsResolvingCurrentLocation(true);
      setLocationError('');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            try {
              const resolvedLocation = await reverseGeocode(latitude, longitude);
              onSelect(resolvedLocation);
            } catch (error) {
              onSelect({
                id: `coords-${latitude}-${longitude}`,
                name: 'Current Location',
                lat: latitude,
                lng: longitude,
                address: '',
              });
            }
            resetModalState();
          } catch (error) {
            console.error('Error handling current location:', error);
            setLocationError('Error processing location. Please try again.');
            setIsResolvingCurrentLocation(false);
          }
        },
        () => {
          setIsResolvingCurrentLocation(false);
          setLocationError('Unable to get current location. Please enable location services.');
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  const handleManualLocation = async (e) => {
    e.preventDefault();

    const trimmedManualLocation = manualLocation.trim();

    if (!trimmedManualLocation) {
      setLocationError('Please enter a location to continue.');
      return;
    }

    try {
      setIsResolvingManualLocation(true);
      setLocationError('');
      const results = await searchLocations(trimmedManualLocation, 1);

      if (!results.length) {
        setLocationError('No matching location found. Please refine the address.');
        setIsResolvingManualLocation(false);
        return;
      }

      try {
        onSelect({ ...results[0], address: trimmedManualLocation + (results[0].address && !trimmedManualLocation.includes(results[0].address) ? ', ' + results[0].address : '') });
        resetModalState();
      } catch (error) {
        console.error('Error during location selection:', error);
        setLocationError('Error saving location. Please try again.');
        setIsResolvingManualLocation(false);
      }
    } catch (error) {
      setLocationError(error.message || 'Unable to resolve the entered location.');
      setIsResolvingManualLocation(false);
    }
  };

  const handleSelectLocation = (loc) => {
    try {
      onSelect(loc);
      resetModalState();
    } catch (error) {
      console.error('Error during location selection:', error);
      setLocationError('Error saving location. Please try again.');
    }
  };

  return (
    <>
      <Form.Group>
        <Form.Label className="fw-bold">{label}</Form.Label>
        <div className="selector-trigger-wrap">
          <Form.Control
            type="text"
            value={location?.name || ''}
            placeholder="Select location"
            readOnly
            onClick={() => setShowModal(true)}
            className="bg-light selector-display selector-input-trigger"
            style={{ cursor: 'pointer' }}
          />
        </div>
      </Form.Group>

      <Modal show={showModal} onHide={resetModalState} size="lg" dialogClassName="selector-modal-shell">
        <Modal.Header closeButton>
          <Modal.Title>Choose {label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {locationError && <Alert variant="danger">{locationError}</Alert>}

          <div className="mb-3">
            <Button
              variant="info"
              className="w-100 mb-3"
              onClick={handleCurrentLocation}
              disabled={isResolvingCurrentLocation}
            >
              <FaLocationArrow className="me-2" />
              {isResolvingCurrentLocation ? 'Detecting Current Location...' : 'Use Current Location'}
            </Button>
          </div>

          <hr className="my-3" />

          <Form.Group className="mb-3">
            <Form.Label>Search Locations</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchTouched(true);
              }}
            />
          </Form.Group>

          {isSearching && <div className="text-muted mb-3">Searching locations...</div>}

          {!isSearching && suggestions.length > 0 && (
            <ListGroup className="mb-3 selector-list-group">
              {suggestions.map((suggestion) => (
                <ListGroup.Item
                  key={suggestion.id}
                  action
                  onClick={() => handleSelectLocation(suggestion)}
                  className="cursor-pointer"
                >
                  <FaMapMarkerAlt className="me-2 text-danger" />
                  {suggestion.name}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {searchTouched && !isSearching && searchQuery.trim().length >= 3 && suggestions.length === 0 && !locationError && (
            <div className="text-muted mb-3">No matching locations found.</div>
          )}

          <hr className="my-3" />

          <Form onSubmit={handleManualLocation}>
            <Form.Group className="mb-2">
              <Form.Label>Or Enter Location Manually</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter address, landmark, or location name"
                value={manualLocation}
                onChange={(e) => setManualLocation(e.target.value)}
              />
            </Form.Group>
            <Button variant="success" type="submit" className="w-100" disabled={isResolvingManualLocation}>
              {isResolvingManualLocation ? 'Resolving Location...' : 'Add Custom Location'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LocationSelector;
